import {Color} from "./enums/color";
import {EventCalendarType} from "./enums/eventCalendarType";
import {IconSource} from "./enums/iconSource";

/**
 * Display details for an event catalogue / calendar type.
 */
export interface IEventCalendarTypeDisplay {
  /** Translation key for the catalogue title, e.g. "Enhanced Catalogue". */
  catalogueTitleKey: string;
  /** Translation key for the "all templates" title in the create event modal. */
  templatesTitleKey: string;
  /** Translation key for the short type label, e.g. "Enhanced". */
  labelKey: string;
  icon: IconSource;
  haloColor: Color;
}

export const eventCalendarTypeDisplay: Record<
  EventCalendarType,
  IEventCalendarTypeDisplay
> = {
  [EventCalendarType.Enhanced]: {
    catalogueTitleKey: "translation:titles.enhancedCatalogue",
    templatesTitleKey: "event:titles:enhancedTemplates",
    labelKey: "eventTemplate:labels.enhanced",
    icon: IconSource.featured,
    haloColor: Color.Purple
  },
  [EventCalendarType.Engage]: {
    catalogueTitleKey: "translation:titles.engageCatalogue",
    templatesTitleKey: "event:titles:engageTemplates",
    labelKey: "eventTemplate:labels.engage",
    icon: IconSource.favourite,
    haloColor: Color.Blue
  },
  [EventCalendarType.Wellbeing]: {
    catalogueTitleKey: "translation:titles.wellbeingCatalogue",
    templatesTitleKey: "event:titles:wellbeingTemplates",
    labelKey: "eventTemplate:labels.wellbeing",
    icon: IconSource.favourite,
    haloColor: Color.Teal
  }
};

/**
 * Gets the display details for a calendar type, defaulting to Enhanced when unknown.
 */
export const getEventCalendarTypeDisplay = (
  calendarType?: EventCalendarType | string | null
): IEventCalendarTypeDisplay =>
  eventCalendarTypeDisplay[
    toEventCalendarType(calendarType) ?? EventCalendarType.Enhanced
  ];

/**
 * Whether the calendar type supports the full (Enhanced) feature set: template description,
 * duration, image and village favourites, plus event end times, release dates and extra fields.
 */
export const hasEnhancedFeatures = (
  calendarType?: EventCalendarType | string | null
): boolean =>
  calendarType === EventCalendarType.Enhanced ||
  calendarType === EventCalendarType.Wellbeing;

/**
 * Parses a calendar type from a value such as the event catalogue "type" query param,
 * which may hold either a calendar type or a (URL encoded) catalogue name.
 * Defaults to Enhanced.
 */
export const eventCalendarTypeFromCatalogueParam = (
  value?: string | null
): EventCalendarType => {
  if (value?.includes(EventCalendarType.Wellbeing)) {
    return EventCalendarType.Wellbeing;
  }
  if (value?.includes(EventCalendarType.Engage)) {
    return EventCalendarType.Engage;
  }
  return EventCalendarType.Enhanced;
};

/**
 * Parses a calendar type from a persisted value, returning undefined when unrecognised.
 */
export const toEventCalendarType = (
  value?: string | null
): EventCalendarType | undefined =>
  Object.values(EventCalendarType).find((type) => type === value);
